import {
  NEWCOMERS,
  type Newcomer,
  type NewcomerGame,
  type NewcomersView,
  REPLAY_API,
  type ReplayCommand,
  type ReplayEvent,
} from '@gardener/protocol';
import { createResource, createSignal, For, Match, onCleanup, Show, Switch } from 'solid-js';
import { blob, call, replayPlayer } from './api';
import { GAME } from './map-shoot';
import { go, route } from './nav';
import { Avatar } from './players';
import {
  ago,
  EmptyState,
  Icon,
  LoadingLine,
  Segmented,
  TOOLBAR_ICON_CONTROL,
  Toolbar,
  ToolbarGroup,
  Well,
  when,
} from './ui';

const RANGES = [
  ['1', 'Today'],
  ['10', '10 days'],
  ['30', '30 days'],
  ['90', '90 days'],
] as const;
type Range = (typeof RANGES)[number][0];

const SPEEDS = [
  ['1', '1×'],
  ['2', '2×'],
  ['4', '4×'],
] as const;

/** What each game is called, whole and on a pip. */
const MODES: Record<string, readonly [string, string]> = {
  royale: ['Battle Royale', 'BR'],
  zombies: ['Zombie Nights', 'ZN'],
  range: ['Shooting range', 'SR'],
};
const modeOf = (g: NewcomerGame) => MODES[g.mode ?? ''] ?? ['A game', '?'];

const TONE: Record<NewcomerGame['outcome'], string> = {
  won: 'var(--success)',
  lost: 'var(--fg-muted)',
  left: 'var(--warning)',
};
const BADGE: Record<NewcomerGame['outcome'], 'success' | 'neutral' | 'warning'> = {
  won: 'success',
  lost: 'neutral',
  left: 'warning',
};

function result(g: NewcomerGame): string {
  const word = { won: 'Won', lost: 'Lost', left: 'Left' }[g.outcome];
  // `days`: the nights a Zombie Nights run survived, so it fell or was left on the next one.
  if (g.days === null) return word;
  return g.outcome === 'won' ? `${word} after night ${g.days}` : `${word} on night ${g.days + 1}`;
}

/** Seconds as `4:05`. */
const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/** The build that plays a game: its own, kept (ADR-0234); on a dev machine, the dev stack's client when it isn't. */
function playerOf(g: NewcomerGame): string | null {
  if (!g.replay) return null;
  if (g.archived && g.build) return replayPlayer(g.build);
  return location.hostname === 'localhost' ? `${GAME}/?replay` : null;
}

type Loaded = Extract<ReplayEvent, { replay: 'loaded' }>;
type Progress = Extract<ReplayEvent, { replay: 'progress' }>;
type Ended = Extract<ReplayEvent, { replay: 'ended' }>;

/**
 * One game played (ADR-0234): nothing is loaded till play is pressed, then the build that played it in an iframe, handed
 * its tape by the admin token (the bytes reveal the bots: never a URL the frame could read), driven by postMessage API v1.
 * Keyed by the tape: another game is another frame.
 */
function Player(props: { tape: string; src: string; account: string }) {
  const origin = new URL(props.src, location.href).origin;
  let frame: HTMLIFrameElement | undefined;
  let stage: HTMLDivElement | undefined;
  const [started, setStarted] = createSignal(false);
  const [info, setInfo] = createSignal<Loaded | null>(null);
  const [at, setAt] = createSignal<Progress | null>(null);
  const [ended, setEnded] = createSignal<Ended | null>(null);
  const [failed, setFailed] = createSignal('');
  const [speed, setSpeed] = createSignal<(typeof SPEEDS)[number][0]>('1');
  const [follow, setFollow] = createSignal(props.account);
  /** The rail's tick while it is dragged. */
  const [dragged, setDragged] = createSignal<number | null>(null);

  const send = (c: ReplayCommand, transfer: Transferable[] = []) =>
    frame?.contentWindow?.postMessage(c, origin, transfer);

  async function hand() {
    try {
      const tape = new Uint8Array(await (await blob(`/api/admin/tapes/${props.tape}`)).arrayBuffer());
      send({ replay: 'load', v: REPLAY_API, tape, follow: props.account, speed: Number(speed()) }, [tape.buffer]);
    } catch (err) {
      setFailed(`The tape could not be fetched: ${(err as Error).message}`);
    }
  }

  const heard = (e: MessageEvent<ReplayEvent>) => {
    if (e.origin !== origin || e.source !== frame?.contentWindow) return;
    switch (e.data?.replay) {
      case 'ready':
        void hand();
        break;
      case 'loaded':
        setInfo(e.data);
        setFollow(e.data.follow ?? props.account);
        break;
      case 'progress':
        setAt(e.data);
        if (e.data.playing || e.data.seeking) setEnded(null);
        break;
      case 'ended':
        setEnded(e.data);
        break;
      case 'error':
        setFailed(e.data.message);
        break;
    }
  };
  addEventListener('message', heard);
  onCleanup(() => removeEventListener('message', heard));

  const seat = () => info()?.seats.find((s) => s.account === follow());
  const from = () => seat()?.from ?? 0;
  const to = () => seat()?.to ?? info()?.ticks ?? 0;
  const seconds = (ticks: number) => ticks / (info()?.tickRate ?? 30);
  const tick = () => dragged() ?? ended()?.tick ?? at()?.tick ?? from();
  const playing = () => at()?.playing ?? false;

  function toggle() {
    if (!started()) return setStarted(true);
    if (!info()) return;
    if (playing()) return send({ replay: 'pause' });
    // Its end, or theirs: from their start again.
    if (ended()) {
      setEnded(null);
      send({ replay: 'seek', tick: from() });
    }
    send({ replay: 'play' });
  }

  const ENDS: Record<Ended['why'], string> = {
    left: 'They left the game here',
    over: 'The game ends here',
    crashed: 'The match crashed here',
  };

  return (
    <>
      <div ref={stage} class="edge relative aspect-video overflow-hidden rounded-xl bg-black">
        <Show when={started()}>
          <iframe
            ref={frame}
            src={props.src}
            title="The game, replayed by the build that played it"
            tabIndex={-1}
            allow="webgpu"
            class="pointer-events-none absolute inset-0 size-full border-0"
          />
        </Show>
        <button
          type="button"
          class="focus-visible:outline-brand absolute inset-0 flex cursor-pointer items-center justify-center focus-visible:outline-2 focus-visible:-outline-offset-2"
          aria-label={playing() ? 'Pause' : 'Play'}
          onClick={toggle}
        >
          <Show when={!started() || (info() && !playing() && !at()?.seeking)}>
            <span class="bg-surface-inverse-bg text-fg-inverse flex size-12 items-center justify-center rounded-full">
              <Icon name="play" class="size-5 translate-x-px" />
            </span>
          </Show>
        </button>
      </div>

      <div class="flex items-center gap-3 px-2">
        <button
          type="button"
          class="text-fg-secondary hover:text-fg-default flex size-7 cursor-pointer items-center justify-center rounded-md"
          aria-label={playing() ? 'Pause' : 'Play'}
          onClick={toggle}
        >
          <Icon name={playing() ? 'pause' : 'play'} class="size-3.5" />
        </button>
        <span class="text-fg-muted w-20 shrink-0 text-xs tabular-nums">
          {clock(seconds(Math.max(0, tick() - from())))} / {clock(seconds(Math.max(0, to() - from())))}
        </span>
        <input
          type="range"
          class="accent-brand min-w-0 flex-1 cursor-pointer"
          aria-label="Where in the game"
          disabled={!info()}
          min={from()}
          max={to()}
          step={1}
          value={tick()}
          onInput={(e) => setDragged(e.currentTarget.valueAsNumber)}
          onChange={(e) => {
            // Read first: `setDragged` rewrites the rail's value at once, to the tick the replay is at.
            const where = e.currentTarget.valueAsNumber;
            setDragged(null);
            setEnded(null);
            setAt((a) => a && { ...a, tick: where });
            send({ replay: 'seek', tick: where });
          }}
        />
        <Segmented
          value={speed()}
          options={SPEEDS}
          onChange={(x) => {
            setSpeed(x);
            send({ replay: 'speed', x: Number(x) });
          }}
        />
        <button
          type="button"
          class="text-fg-secondary hover:text-fg-default flex size-7 cursor-pointer items-center justify-center rounded-md disabled:cursor-default disabled:opacity-40"
          title="Fullscreen"
          aria-label="Fullscreen"
          disabled={!started()}
          onClick={() => void stage?.requestFullscreen()}
        >
          <Icon name="frame" class="size-3.5" />
        </button>
      </div>

      <Show when={(info()?.seats.length ?? 0) > 1}>
        <div class="flex items-center gap-2 px-2">
          <select
            class="ld-select w-60 rounded-full"
            data-size="sm"
            aria-label="Watching"
            value={follow()}
            onChange={(e) => {
              setFollow(e.currentTarget.value);
              setEnded(null);
              send({ replay: 'view', follow: e.currentTarget.value });
            }}
          >
            <For each={info()?.seats ?? []}>{(s) => <option value={s.account}>Watching {s.name}</option>}</For>
          </select>
        </div>
      </Show>

      <p class="text-fg-muted flex min-w-0 items-center gap-1.5 px-3 pb-1 text-xs">
        <Switch
          fallback={
            <>
              <span class="ld-status-dot" data-variant="warning" aria-hidden="true" />
              Press play: your browser replays the tape with the build that played it.
            </>
          }
        >
          <Match when={failed()}>
            {(message) => (
              <>
                <span class="ld-status-dot" data-variant="error" aria-hidden="true" />
                <span role="alert">{message()}</span>
              </>
            )}
          </Match>
          <Match when={at()?.diverged != null}>
            <span class="ld-status-dot" data-variant="error" aria-hidden="true" />
            Not the game that was played: its checksums differ from {clock(seconds(at()?.diverged ?? 0))}.
          </Match>
          <Match when={ended()}>
            {(end) => (
              <>
                <span
                  class="ld-status-dot"
                  data-variant={end().why === 'crashed' ? 'error' : 'success'}
                  aria-hidden="true"
                />
                {ENDS[end().why]}, {clock(seconds(Math.max(0, end().tick - from())))} in. {at()?.checks ?? 0} checksums
                matched.
              </>
            )}
          </Match>
          <Match when={at()?.seeking}>
            <span class="ld-spinner" data-size="xs" aria-hidden="true" />
            Replaying up to there
          </Match>
          <Match when={info()}>
            <span class="ld-status-dot" data-variant="success" aria-hidden="true" />
            Exact: {at()?.checks ?? 0} checksums matched so far.
          </Match>
          <Match when={started()}>
            <span class="ld-spinner" data-size="xs" aria-hidden="true" />
            Loading the build that played it
          </Match>
        </Switch>
      </p>
    </>
  );
}

/** One newcomer: who, a tab per game, the game. */
function Detail(props: { p: Newcomer }) {
  const [shown, setShown] = createSignal('0');
  const game = () => props.p.games[Number(shown())];
  const missing = () => NEWCOMERS.games - props.p.games.length;
  return (
    <>
      <Well label={props.p.name}>
        <div class="flex items-center gap-3 px-2 py-1.5">
          <Avatar p={props.p} large />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium">{props.p.name}</p>
            <p class="text-fg-muted truncate text-xs">
              Signed up {when(props.p.createdAt)} · {props.p.games.length}{' '}
              {props.p.games.length === 1 ? 'game' : 'games'} so far
            </p>
          </div>
          <button type="button" class="ld-button shrink-0" data-size="sm" onClick={() => go('players', props.p.id)}>
            Open in Players
          </button>
        </div>
        <Show when={props.p.games.length}>
          <div class="flex items-center gap-2 px-2 pb-1">
            <Segmented
              value={shown()}
              options={props.p.games.map((g, k) => [String(k), `Game ${k + 1} · ${modeOf(g)[1]}`] as const)}
              onChange={setShown}
            />
          </div>
        </Show>
      </Well>

      <Show when={game()}>
        {(g) => (
          <Well
            title={`Game ${Number(shown()) + 1} · ${modeOf(g())[0]}`}
            actions={
              <>
                <span class="ld-badge" data-size="sm" data-variant={BADGE[g().outcome]}>
                  {result(g())}
                </span>
                <span class="text-fg-muted px-2 text-xs tabular-nums" title={when(g().endedAt)}>
                  {ago(g().endedAt)}
                </span>
              </>
            }
          >
            <Show
              when={g().replay?.tape}
              keyed
              fallback={
                <NoGame
                  title="Nothing to watch"
                  why="It has no tape: a game played offline, or on a server without a bucket."
                />
              }
            >
              {(tape) => (
                <Show
                  when={playerOf(g())}
                  fallback={
                    <NoGame
                      title="Its build isn’t kept"
                      why={`Build ${g().build ?? 'unknown'} played this game, and only the build that played a game can replay it. Builds are kept from the replay player’s first deploy on.`}
                    />
                  }
                >
                  {(src) => <Player tape={tape} src={src()} account={props.p.id} />}
                </Show>
              )}
            </Show>
          </Well>
        )}
      </Show>

      <Show when={missing() > 0}>
        <p class="text-fg-muted px-3 text-xs">
          {missing() === 1 ? 'One more game' : `${missing()} more games`} will show here if they play again.
        </p>
      </Show>
    </>
  );
}

function NoGame(props: { title: string; why: string }) {
  return (
    <div class="bg-surface-50-bg edge flex aspect-video flex-col items-center justify-center gap-2 rounded-xl px-8 text-center">
      <p class="text-fg-secondary text-sm font-medium">{props.title}</p>
      <p class="text-fg-muted max-w-sm text-xs">{props.why}</p>
    </div>
  );
}

/**
 * Newcomers (ADR-0234): the accounts that signed up lately, the latest first, each with their first games on the server;
 * a game watched here is its tape replayed in this browser by the build that played it, proven by the tape's checksums.
 * One opened is in the address (`#newcomers/<id>`).
 */
export function Newcomers() {
  const [days, setDays] = createSignal<Range>('10');
  const [list, { refetch }] = createResource(days, (d) => call<NewcomersView>('GET', `/api/admin/newcomers?days=${d}`));
  // A resource read while it failed throws (and takes the app down): ask for its error first.
  const rows = () => (list.error ? [] : (list()?.newcomers ?? []));
  const open = () => rows().find((p) => p.id === route().arg) ?? rows()[0];

  return (
    <div class="@container flex w-full flex-col">
      <Toolbar>
        <ToolbarGroup label="Signed up" value={days()} options={RANGES} onChange={setDays} />
        <button
          type="button"
          class={TOOLBAR_ICON_CONTROL}
          title="Refresh"
          aria-label="Refresh"
          onClick={() => refetch()}
        >
          <Icon name="refresh" class={`size-3.5 ${list.loading ? 'animate-spin' : ''}`} />
        </button>
      </Toolbar>
      <div class="grid items-start gap-2 p-2 min-[1180px]:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
        <Well
          title="Signed up, the latest first"
          actions={
            <span class="text-fg-muted px-3 text-xs tabular-nums">
              {rows().length === 1 ? '1 account' : `${rows().length} accounts`}
            </span>
          }
        >
          <Switch
            fallback={
              <table class="w-full table-fixed text-left text-sm">
                <thead class="text-fg-muted text-xs">
                  <tr>
                    <th scope="col" class="h-6 px-3 font-normal">
                      Player
                    </th>
                    <th scope="col" class="h-6 w-28 px-3 font-normal">
                      First {NEWCOMERS.games}
                    </th>
                    <th scope="col" class="h-6 w-32 px-3 font-normal">
                      Signed up
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <For each={rows()}>
                    {(p) => (
                      <tr
                        class={`group relative ${open()?.id === p.id ? 'bg-surface-25-selected-bg' : 'hover:bg-surface-25-hover-bg'}`}
                        aria-current={open()?.id === p.id ? 'true' : undefined}
                      >
                        <td class="rounded-l-lg px-3 py-2">
                          <div class="flex min-w-0 items-center gap-3">
                            <Avatar p={p} />
                            <button
                              type="button"
                              class="block max-w-full min-w-0 cursor-pointer truncate text-left font-medium after:absolute after:inset-0 group-hover:underline"
                              onClick={() => go('newcomers', p.id)}
                            >
                              {p.name}
                            </button>
                          </div>
                        </td>
                        <td class="px-3 py-2">
                          <div class="flex items-center gap-1">
                            <For each={Array.from({ length: NEWCOMERS.games }, (_, k) => p.games[k])}>
                              {(g) => (
                                <Show when={g} fallback={<span class="bg-surface-100-bg size-5.5 rounded-md" />}>
                                  {(g) => (
                                    <span
                                      class="flex size-5.5 items-center justify-center rounded-md text-[10px] font-semibold"
                                      title={`${modeOf(g())[0]} · ${result(g())}`}
                                      style={{
                                        background: `oklch(from ${TONE[g().outcome]} l c h / 0.18)`,
                                        color: TONE[g().outcome],
                                      }}
                                    >
                                      {modeOf(g())[1]}
                                    </span>
                                  )}
                                </Show>
                              )}
                            </For>
                          </div>
                        </td>
                        <td class="text-fg-tertiary truncate rounded-r-lg px-3 py-2" title={when(p.createdAt)}>
                          {ago(p.createdAt)}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            }
          >
            <Match when={list.error}>
              {/* logdash's failed list (`TeamManagementCard`): a muted line after a danger sign. */}
              <div class="text-fg-muted flex items-center gap-2 px-3 py-2.5 text-[13px]" role="alert">
                <Icon name="danger" class="size-4 shrink-0" />
                Couldn’t load the newcomers
              </div>
            </Match>
            <Match when={list.loading && !rows().length}>
              <div class="px-3 py-2.5">
                <LoadingLine label="Loading newcomers" />
              </div>
            </Match>
            <Match when={!rows().length}>
              <EmptyState centered title="Nobody new" description="No account signed up in this time." />
            </Match>
          </Switch>
          <p class="text-fg-muted px-3 pb-1 text-xs">
            The population’s bots and admins left out. A game played offline has no tape, so nothing to watch.
          </p>
        </Well>

        <div class="flex min-w-0 flex-col gap-2 min-[1180px]:sticky min-[1180px]:top-2">
          {/* Keyed by the account: another one starts on its first game, its frame gone. */}
          <Show when={open()?.id} keyed>
            {(id) => <Detail p={rows().find((p) => p.id === id) as Newcomer} />}
          </Show>
        </div>
      </div>
    </div>
  );
}
